import type { BookingDeposit } from '../api/bookings';

/**
 * How the deposit splits across a document: owed now, already held, and coming back.
 *
 * <p>
 * <strong>The deposit is added to what a customer pays, never subtracted from it.</strong> That
 * reads backwards until you say it out loud: the customer <em>hands the deposit over</em> at
 * collection and gets it back at return, so on the day they collect it is money going out. Netting
 * it off would print a negative — "pay −11,395" — and tell them the shop owes them money before
 * they have taken a single garment.
 * </p>
 *
 * <p>
 * It <em>is</em> subtracted, on the other document. The refund note at return settles it the other
 * way: held, less damage, less late fee, less missing accessories, balance refunded. Two documents,
 * two directions, and this function exists so the booking copy can show both without confusing
 * them — what you pay today, and what comes back after.
 * </p>
 *
 * <p>
 * <strong>Whether the deposit is owed at all depends on when the shop collects it.</strong> A shop
 * that takes it at booking has already been paid, so adding it again at collection charges it
 * twice — which is exactly what the printed copy did before the collection point became a setting.
 * </p>
 */
export interface DepositSplit {
  /** Payable at collection, because it has not been taken yet. */
  due: number;
  /** Already in the shop's hands. Shown so the slip does not look as though it has vanished. */
  held: number;
  /** Expected back at return, before any deduction. Zero once settled, either way. */
  refundable: number;
}

export function depositSplit(deposit: BookingDeposit): DepositSplit {
  const amount = deposit.amount ?? 0;

  if (amount <= 0) {
    return { due: 0, held: 0, refundable: 0 };
  }

  const taken = deposit.state !== 'NotTaken';

  // A settled deposit is finished business: refunded money has gone back and forfeited money has
  // become taxable revenue. Neither is still owed to the customer.
  const settled =
    deposit.state === 'Refunded' ||
    deposit.state === 'Forfeited' ||
    deposit.state === 'AppliedToBalance';

  return {
    due: taken ? 0 : amount,
    held: taken ? amount : 0,
    refundable: settled ? 0 : amount,
  };
}
